import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  DebtDto,
  DebtListResponse,
  CreateDebtRequest,
  UpdateDebtRequest,
  SettleDebtRequest,
} from '@signalvote/shared';
import { apiJson, type ApiErrorBody } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';

/** The server's `{ error }` message (an empty one included), else the fallback. */
function serverError(fallback: string) {
  return (body: ApiErrorBody) => new Error(body.error ?? fallback);
}

// ADR-031 (GitHub #92) — the non-loan Schedule D debt write-path (federal-only). A debt is a
// living treasurer-maintained obligation owed BY the committee: recorded once, then paid down
// by tagging ordinary expenditures (Expenditure.DebtId) — its SD10 begin/incurred/payment/end
// columns and Form 3 line 10 are computed from those dated payments, never a stored balance.

/** GET /api/finance/debts — list the campaign's debts (newest incur date first). */
export function useFinanceDebts() {
  return useQuery({
    queryKey: queryKeys.finance.debts(),
    queryFn: async (): Promise<DebtDto[]> => {
      const body = await apiJson<DebtListResponse>('/api/finance/debts', { error: 'Failed to load debts' });
      return body.items;
    },
  });
}

/** POST /api/finance/debts — record a new debt owed BY the committee (a non-cash gesture). */
export function useCreateDebt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateDebtRequest): Promise<DebtDto> =>
      apiJson<DebtDto>('/api/finance/debts', {
        method: 'POST',
        json: data,
        error: serverError('Failed to record debt'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.debts() });
    },
  });
}

/**
 * PATCH /api/finance/debts/{id} — edit a debt's incurred amount / date / purpose (and notes).
 * Amount/date/purpose are locked once the incurred period is filed (reopen + amend); notes stay
 * editable regardless.
 */
export function useUpdateDebt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDebtRequest }): Promise<DebtDto> =>
      apiJson<DebtDto>(`/api/finance/debts/${id}`, {
        method: 'PATCH',
        json: data,
        error: serverError('Failed to update debt'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.debts() });
    },
  });
}

/**
 * POST /api/finance/debts/{id}/void. Voids a debt entered in error so it drops from a fresh
 * filing's Schedule D / line 10 (kept for audit). A debt in a filed, still-locked period
 * returns a 400 the caller surfaces. A paid-off debt is NEVER voided — it self-suppresses.
 */
export function useVoidDebt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string): Promise<DebtDto> =>
      apiJson<DebtDto>(`/api/finance/debts/${id}/void`, {
        method: 'POST',
        error: serverError('Failed to void debt'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.debts() });
    },
  });
}

/** POST /api/finance/debts/{id}/unvoid. Restores a voided debt (the inverse). */
export function useUnvoidDebt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string): Promise<DebtDto> =>
      apiJson<DebtDto>(`/api/finance/debts/${id}/unvoid`, {
        method: 'POST',
        error: serverError('Failed to restore debt'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.debts() });
    },
  });
}

/**
 * POST /api/finance/debts/{id}/settle. Records a debt as settled for less than its face amount,
 * or forgiven by the creditor, capturing what they accepted and why (52 U.S.C. 30104(b)(8)). The
 * incurred amount is untouched — the remainder is written off as of the settlement date, which is
 * what takes the debt out of Schedule D and line 10 from that period on. A settlement dated into
 * a filed, still-locked period returns a 400 the caller surfaces, as does an amount the debt's
 * recorded payments do not support.
 */
export function useSettleDebt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SettleDebtRequest }): Promise<DebtDto> =>
      apiJson<DebtDto>(`/api/finance/debts/${id}/settle`, {
        method: 'POST',
        json: data,
        error: serverError('Failed to record the settlement'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.debts() });
    },
  });
}

/** POST /api/finance/debts/{id}/unsettle. Undoes a settlement recorded in error (the inverse). */
export function useUnsettleDebt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string): Promise<DebtDto> =>
      apiJson<DebtDto>(`/api/finance/debts/${id}/unsettle`, {
        method: 'POST',
        error: serverError('Failed to undo the settlement'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.debts() });
    },
  });
}
